import type { TranslationFn } from "@/utils/countries";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { flatMap, sumBy, uniq } from "lodash-es";
import type { CompanyOrder } from "../common/companyOrderTypes";
import {
  areEventRowsVisible,
  selectedEventRowTypes,
  selectedOrderTypes,
  type TransactionFilters,
} from "./transactionFilters";
import {
  eventRowType,
  type RegistryEventSummary,
  type RegistryEventType,
  type SortOrder,
} from "./transactionLogTypes";
import {
  eventTypeIcon,
  formatEventGroupRowType,
  formatEventRowType,
  formatOrderType,
  orderTypeIcon,
} from "./transactionTypePresentation";

/** Sent to the events endpoint as `gap_minutes`, so a page never ends inside a group. */
export const EVENT_GROUP_WINDOW_MINUTES = 60;

const EVENT_GROUP_WINDOW_MS = EVENT_GROUP_WINDOW_MINUTES * 60 * 1000;

/**
 * What the table shows for every row kind.
 *
 * @property at - The row's place on the timeline: order creation, event occurrence, or a group's first event
 * @property portfolioName - An order's source portfolio, or the portfolios an allocated or returned transfer landed in
 */
interface RowDisplay {
  at: string;
  companyName: string | null;
  portfolioName: string | null;
  volume: number;
  typeLabel: string;
  icon: IconProp;
}

export interface OrderRow extends RowDisplay {
  kind: "order";
  key: string;
  order: CompanyOrder;
}

export interface EventRow extends RowDisplay {
  kind: "event";
  key: string;
  event: RegistryEventSummary;
}

/** Two or more consecutive unconnected events of one company and type (requirement B6). */
export interface EventGroupRow extends RowDisplay {
  kind: "event_group";
  key: string;
  eventType: RegistryEventType;
  /** Members in ascending occurrence. */
  events: RegistryEventSummary[];
  endedAt: string;
}

export type TransactionRow = OrderRow | EventRow | EventGroupRow;

/**
 * @property allEventsLoaded - The events endpoint has no further page for the current query
 */
export interface TransactionRowsInput {
  orders: CompanyOrder[];
  events: RegistryEventSummary[];
  filters: TransactionFilters;
  sortOrder: SortOrder;
  allEventsLoaded: boolean;
  t: TranslationFn;
}

/**
 * Merges orders and unconnected events into the Transactions table's rows, in `sortOrder` by date.
 *
 * Orders beyond the last loaded event are held back until more events load, so the table never
 * shows old orders with the events between them missing. Events are grouped after filtering, in
 * ascending order, over the rows the table shows: a hidden row never splits a group.
 */
export function buildTransactionRows(
  input: TransactionRowsInput
): TransactionRow[] {
  const showEvents = areEventRowsVisible(input.filters);
  const filteredOrders = filterOrders(input.orders, input.filters);
  const orders =
    showEvents && !input.allEventsLoaded
      ? holdBackOrdersBeyondLoadedEvents(
          filteredOrders,
          input.events,
          input.sortOrder
        )
      : filteredOrders;
  const events = showEvents ? filterEvents(input.events, input.filters) : [];

  const ascending = sortAscending([
    ...orders.map((order) => toOrderRow(order, input.t)),
    ...events.map((event) => toEventRow(event, input.t)),
  ]);
  const grouped = groupEvents(ascending, input.t);
  return input.sortOrder === "asc" ? grouped : grouped.reverse();
}

/** The orders matching the filters, with no date cut-off applied. */
export function filterOrders(
  orders: CompanyOrder[],
  filters: TransactionFilters
): CompanyOrder[] {
  const orderTypes = selectedOrderTypes(filters);
  if (filters.types.length > 0 && orderTypes.length === 0) return [];
  return orders.filter(
    (order) =>
      (orderTypes.length === 0 || orderTypes.includes(order.order_type)) &&
      (filters.statuses.length === 0 ||
        filters.statuses.includes(order.status)) &&
      (filters.portfolios.length === 0 ||
        filters.portfolios.includes(order.source_portfolio_name ?? "")) &&
      containsText(order.order_starter_name, filters.starter) &&
      containsText(order.company_name, filters.companyName)
  );
}

function filterEvents(
  events: RegistryEventSummary[],
  filters: TransactionFilters
): RegistryEventSummary[] {
  const eventTypes = selectedEventRowTypes(filters);
  return events.filter(
    (event) =>
      (eventTypes.length === 0 || eventTypes.includes(eventRowType(event))) &&
      (filters.portfolios.length === 0 ||
        (event.allocation?.portfolio_names ?? []).some((name) =>
          filters.portfolios.includes(name)
        )) &&
      containsText(event.company_name, filters.companyName)
  );
}

function containsText(value: string | null | undefined, query: string) {
  const needle = query.trim().toLocaleLowerCase();
  return needle === "" || (value ?? "").toLocaleLowerCase().includes(needle);
}

function holdBackOrdersBeyondLoadedEvents(
  orders: CompanyOrder[],
  loadedEvents: RegistryEventSummary[],
  sortOrder: SortOrder
): CompanyOrder[] {
  const lastLoaded = loadedEvents.at(-1);
  if (!lastLoaded) return orders;
  const boundary = Date.parse(lastLoaded.occurred_at);
  return orders.filter((order) => {
    const createdAt = Date.parse(order.created_at);
    return sortOrder === "desc" ? createdAt >= boundary : createdAt <= boundary;
  });
}

function toOrderRow(order: CompanyOrder, t: TranslationFn): OrderRow {
  return {
    kind: "order",
    key: `order-${order.id}`,
    order,
    at: order.created_at,
    companyName: order.company_name ?? null,
    portfolioName: order.source_portfolio_name ?? null,
    volume: order.total_selected_mwh,
    typeLabel: formatOrderType(order.order_type, t),
    icon: orderTypeIcon(order.order_type),
  };
}

function toEventRow(event: RegistryEventSummary, t: TranslationFn): EventRow {
  return {
    kind: "event",
    key: `event-${event.id}`,
    event,
    at: event.occurred_at,
    companyName: event.company_name,
    portfolioName: joinPortfolioNames([event]),
    volume: event.linked_volume,
    typeLabel: formatEventRowType(eventRowType(event), t),
    icon: eventTypeIcon(eventRowType(event)),
  };
}

function sortAscending(rows: (OrderRow | EventRow)[]): (OrderRow | EventRow)[] {
  return [...rows].sort(
    (a, b) =>
      Date.parse(a.at) - Date.parse(b.at) ||
      a.kind.localeCompare(b.kind) ||
      rowId(a) - rowId(b)
  );
}

function rowId(row: OrderRow | EventRow): number {
  return row.kind === "order" ? row.order.id : row.event.id;
}

function groupEvents(
  ascending: (OrderRow | EventRow)[],
  t: TranslationFn
): TransactionRow[] {
  const rows: TransactionRow[] = [];
  let run: EventRow[] = [];

  const closeRun = () => {
    if (run.length === 1) rows.push(run[0]);
    if (run.length > 1)
      rows.push(
        toEventGroupRow(
          run.map((member) => member.event),
          t
        )
      );
    run = [];
  };

  for (const row of ascending) {
    if (
      row.kind === "event" &&
      run.length > 0 &&
      extendsRun(run[0].event, row.event)
    ) {
      run.push(row);
      continue;
    }
    closeRun();
    if (row.kind === "event") run = [row];
    else rows.push(row);
  }
  closeRun();
  return rows;
}

function extendsRun(
  first: RegistryEventSummary,
  next: RegistryEventSummary
): boolean {
  return (
    next.company_id === first.company_id &&
    eventRowType(next) === eventRowType(first) &&
    Date.parse(next.occurred_at) - Date.parse(first.occurred_at) <=
      EVENT_GROUP_WINDOW_MS
  );
}

function toEventGroupRow(
  events: RegistryEventSummary[],
  t: TranslationFn
): EventGroupRow {
  const first = events[0];
  return {
    kind: "event_group",
    key: `group-${first.id}`,
    eventType: first.event_type,
    events,
    at: first.occurred_at,
    endedAt: events[events.length - 1].occurred_at,
    companyName: first.company_name,
    portfolioName: joinPortfolioNames(events),
    volume: sumBy(events, "linked_volume"),
    typeLabel: formatEventGroupRowType(eventRowType(first), events.length, t),
    icon: eventTypeIcon(eventRowType(first)),
  };
}

function joinPortfolioNames(events: RegistryEventSummary[]): string | null {
  const names = uniq(
    flatMap(events, (event) => event.allocation?.portfolio_names ?? [])
  );
  return names.length > 0 ? names.join(", ") : null;
}
